import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

const resources = {
    en: {
        translation: {
            nav: {
                home: 'Home',
                investment: 'International Investment Consulting',
                construction: 'Construction and Project Management',
                materials: 'Materials and Interior Solutions',
                ourservices: 'Services',
                sustainability: 'Sustainability'
            },
            hero: {
                title: 'Professional Construction Management',
                subtitle: 'Building Excellence with German Precision',
                description: 'DLDMN Baumanagement GmbH delivers comprehensive construction management services with over 15 years of experience in the industry.',
                cta: 'Explore Our Projects',
                contact: 'Get in Touch'
            },
            about: {
                title: 'About DLDMN Baumanagement',
                subtitle: 'Excellence in Construction Management',
                description: 'With headquarters in Germany, DLDMN Baumanagement GmbH has established itself as a leading construction management company, delivering high-quality projects across residential, commercial, and industrial sectors.',
                experience: 'Years of Experience',
                projects: 'Completed Projects',
                clients: 'Satisfied Clients',
                buildingExcellence: 'Building Excellence Since 2008',
                vision: 'Our Vision',
                visionText: 'To be the leading construction management company in Germany, setting industry standards for quality, innovation, and sustainability.',
                mission: 'Our Mission',
                missionText: 'We provide comprehensive construction management services that exceed client expectations while maintaining the highest standards of safety, quality, and environmental responsibility.',
                learnMore: 'Learn More About Us',
                features: {
                    quality: {
                        title: 'Quality Excellence',
                        description: 'German engineering standards and meticulous attention to detail in every project.'
                    },
                    team: {
                        title: 'Expert Team',
                        description: 'Experienced professionals with decades of combined expertise in construction management.'
                    },
                    results: {
                        title: 'Proven Results',
                        description: 'Track record of delivering projects on time and within budget consistently.'
                    }
                }
            },
            services: {
                investment: {
                    title: 'International Investment Consulting',
                    description: 'At DLDMN Baumanagement GmbH, we provide individuals and corporations with professional investment advisory services in Germany and Turkey:',
                    items: [
                        'Real estate acquisition and sales management',
                        'In-depth market research and analysis',
                        'Identification of high-return investment opportunities',
                        'Legal, tax, and administrative follow-up',
                        'Portfolio optimization for maximum profitability',
                        'Development of long-term investment strategies'
                    ],
                    conclusion: 'Our clients benefit not only from investments but also from a secure, transparent, and sustainable growth model.'
                },
                construction: {
                    title: 'Construction and Project Management',
                    description: 'From concept to completion, we manage every stage of your construction project with precision.',
                    items: [
                        'Land development and new construction',
                        'Modernization and renovation projects',
                        'Architectural and engineering solutions',
                        'On-site management and quality assurance',
                        'Turnkey residential and commercial projects'
                    ],
                    conclusion: 'DLDMN Baumanagement GmbH ensures efficiency in time, budget, and quality.'
                },
                materials: {
                    title: 'Construction Materials and Interior Solutions',
                    description: 'With our strong supply network, we provide high-quality materials and interior solutions at competitive prices in both Germany and Turkey:',
                    items: [
                        'Building materials (concrete, steel, insulation, etc.)',
                        'Interior solutions (kitchens, bathrooms, furniture, smart-home systems)',
                        'Customized and tailor-made orders',
                        'Fast delivery from stock and reliable logistics'
                    ],
                    conclusion: 'We are more than a construction partner – we are a solution provider for modern and functional living spaces.'
                }
            },
            sustainability: {
                title: 'Sustainability & Innovation',
                subtitle: 'Building for the Future',
                futureOriented: 'Future-Oriented Construction',
                description: 'We prioritize future-oriented construction with a commitment to sustainability.',
                features: [
                    'Eco-friendly and energy-efficient materials',
                    'Health-conscious exterior and interior solutions',
                    'Innovative building technologies',
                    'Projects focused on reducing carbon footprint'
                ],
                conclusion: 'Every project we deliver reflects the harmony of nature, people, and technology.',
                commitment: 'Our Commitment to Sustainability',
                commitmentSubtitle: 'Detailed approach to building a sustainable future',
                ctaTitle: 'Ready to Build Sustainably?',
                ctaSubtitle: 'Let\'s create a project that benefits both you and the environment.',
                ctaButton: 'Start Your Sustainable Project',
                sections: {
                    materials: {
                        title: 'Sustainable Materials',
                        description: 'We carefully select eco-friendly materials that minimize environmental impact while maximizing durability and performance.'
                    },
                    health: {
                        title: 'Health-Conscious Design',
                        description: 'Our solutions prioritize human health and well-being through thoughtful design and material selection.'
                    },
                    technology: {
                        title: 'Innovative Technologies',
                        description: 'We integrate cutting-edge building technologies to create smarter, more efficient structures.'
                    },
                    carbon: {
                        title: 'Carbon Footprint Reduction',
                        description: 'Every project is designed with carbon reduction in mind, contributing to a more sustainable future.'
                    }
                }
            },
            contact: {
                title: 'Contact Us',
                subtitle: 'Get in Touch for Your Next Project',
                address: 'Address',
                email: 'Email',
                info: {
                    address: 'Markstraße 37, 13409 Berlin',
                    email: 'info@dldmn-baumanagement.de'
                },
                getInTouch: 'Get in Touch',
                projectCard: {
                    title: 'Ready to Start Your Project?',
                    subtitle: 'Let\'s discuss how we can bring your construction vision to life.',
                    consultation: {
                        title: 'Consultation',
                        description: 'Free initial consultation to understand your needs'
                    },
                    planning: {
                        title: 'Planning',
                        description: 'Detailed project planning and cost estimation'
                    },
                    execution: {
                        title: 'Execution',
                        description: 'Professional execution with quality assurance'
                    }
                },
                contactDescription: 'Contact us for your construction and investment needs',
                ourLocation: 'Our Location',
                locationDescription: 'Visit us at our Berlin office',
                viewOnMaps: 'View on Google Maps'
            },
            footer: {
                description: 'DLDMN Baumanagement GmbH - Your trusted partner in construction management.',
                quickLinks: 'Quick Links',
                services: 'Our Services',
                followUs: 'Follow Us',
                rights: 'All rights reserved.',
                servicesList: [
                    'Construction Management',
                    'Project Planning',
                    'Quality Control',
                    'Safety Management',
                    'Cost Estimation',
                    'Consulting Services'
                ]
            },
            aboutServices: {
                title: 'Our Services',
                subtitle: 'Comprehensive solutions for your construction and investment needs'
            }
        }
    },
    de: {
        translation: {
            nav: {
                home: 'Startseite',
                investment: 'Internationale Investitionsberatung',
                construction: 'Bau- und Projektmanagement',
                materials: 'Bau- und Innenausstattung',
                ourservices: 'Dienstleistungen',
                sustainability: 'Nachhaltigkeit'
            },
            hero: {
                title: 'Professionelles Baumanagement',
                subtitle: 'Exzellenz mit deutscher Präzision',
                description: 'DLDMN Baumanagement GmbH bietet umfassende Baumanagement-Dienstleistungen mit über 15 Jahren Branchenerfahrung.',
                cta: 'Unsere Projekte erkunden',
                contact: 'Kontakt aufnehmen'
            },
            about: {
                title: 'Über DLDMN Baumanagement',
                subtitle: 'Exzellenz im Baumanagement',
                description: 'Mit Hauptsitz in Deutschland hat sich die DLDMN Baumanagement GmbH als führendes Baumanagement-Unternehmen etabliert und liefert hochwertige Projekte in den Bereichen Wohnen, Gewerbe und Industrie.',
                experience: 'Jahre Erfahrung',
                projects: 'Abgeschlossene Projekte',
                clients: 'Zufriedene Kunden',
                buildingExcellence: 'Exzellenz im Bauwesen seit 2008',
                vision: 'Unsere Vision',
                visionText: 'Das führende Baumanagement-Unternehmen in Deutschland zu sein und Industriestandards für Qualität, Innovation und Nachhaltigkeit zu setzen.',
                mission: 'Unsere Mission',
                missionText: 'Wir bieten umfassende Baumanagement-Dienstleistungen, die die Erwartungen unserer Kunden übertreffen und dabei höchste Standards in Sicherheit, Qualität und Umweltverantwortung einhalten.',
                learnMore: 'Mehr über uns erfahren',
                features: {
                    quality: {
                        title: 'Qualitätsexzellenz',
                        description: 'Deutsche Ingenieurstandards und akribische Aufmerksamkeit für Details in jedem Projekt.'
                    },
                    team: {
                        title: 'Expertenteam',
                        description: 'Erfahrene Fachkräfte mit jahrzehntelanger kombinierter Expertise im Baumanagement.'
                    },
                    results: {
                        title: 'Bewährte Ergebnisse',
                        description: 'Erfolgsgeschichte bei der pünktlichen und budgetgerechten Projektabwicklung.'
                    }
                }
            },
            services: {
                investment: {
                    title: 'Internationale Investitionsberatung',
                    description: 'Bei DLDMN Baumanagement GmbH bieten wir Privatpersonen und Unternehmen professionelle Investitionsberatung in Deutschland und der Türkei:',
                    items: [
                        'Immobilienerwerb und Verkaufsmanagement',
                        'Tiefgreifende Marktforschung und -analyse',
                        'Identifizierung renditestarker Investitionsmöglichkeiten',
                        'Rechtliche, steuerliche und administrative Betreuung',
                        'Portfolio-Optimierung für maximale Rentabilität',
                        'Entwicklung langfristiger Investitionsstrategien'
                    ],
                    conclusion: 'Unsere Kunden profitieren nicht nur von Investitionen, sondern auch von einem sicheren, transparenten und nachhaltigen Wachstumsmodell.'
                },
                construction: {
                    title: 'Bau- und Projektmanagement',
                    description: 'Von der Konzeption bis zur Fertigstellung verwalten wir jede Phase Ihres Bauprojekts mit Präzision.',
                    items: [
                        'Grundstücksentwicklung und Neubau',
                        'Modernisierungs- und Renovierungsprojekte',
                        'Architektur- und Ingenieursdienstleistungen',
                        'Bauleitung und Qualitätssicherung',
                        'Schlüsselfertige Wohn- und Gewerbeprojekte'
                    ],
                    conclusion: 'DLDMN Baumanagement GmbH gewährleistet Effizienz in Zeit, Budget und Qualität.'
                },
                materials: {
                    title: 'Baumaterialien und Innenausstattung',
                    description: 'Mit unserem starken Lieferantennetzwerk bieten wir hochwertige Materialien und Innenausstattung zu wettbewerbsfähigen Preisen in Deutschland und der Türkei:',
                    items: [
                        'Baumaterialien (Beton, Stahl, Dämmung, etc.)',
                        'Innenausstattung (Küchen, Badezimmer, Möbel, Smart-Home-Systeme)',
                        'Maßgeschneiderte und individuelle Bestellungen',
                        'Schnelle Lieferung ab Lager und zuverlässige Logistik'
                    ],
                    conclusion: 'Wir sind mehr als ein Baupartner – wir sind ein Lösungsanbieter für moderne und funktionale Lebensräume.'
                }
            },
            sustainability: {
                title: 'Nachhaltigkeit & Innovation',
                subtitle: 'Bauen für die Zukunft',
                futureOriented: 'Zukunftsorientiertes Bauen',
                description: 'Wir priorisieren zukunftsorientiertes Bauen mit einem Bekenntnis zur Nachhaltigkeit.',
                features: [
                    'Umweltfreundliche und energieeffiziente Materialien',
                    'Gesundheitsbewusste Außen- und Innenlösungen',
                    'Innovative Bautechnologien',
                    'Projekte mit Fokus auf CO2-Fußabdruck-Reduzierung'
                ],
                conclusion: 'Jedes Projekt, das wir liefern, spiegelt die Harmonie von Natur, Menschen und Technologie wider.',
                commitment: 'Unser Bekenntnis zur Nachhaltigkeit',
                commitmentSubtitle: 'Detaillierter Ansatz für eine nachhaltige Zukunft',
                ctaTitle: 'Bereit, nachhaltig zu bauen?',
                ctaSubtitle: 'Lassen Sie uns ein Projekt schaffen, das sowohl Ihnen als auch der Umwelt zugute kommt.',
                ctaButton: 'Starten Sie Ihr nachhaltiges Projekt',
                sections: {
                    materials: {
                        title: 'Nachhaltige Materialien',
                        description: 'Wir wählen sorgfältig umweltfreundliche Materialien aus, die die Umweltbelastung minimieren und gleichzeitig Haltbarkeit und Leistung maximieren.'
                    },
                    health: {
                        title: 'Gesundheitsbewusstes Design',
                        description: 'Unsere Lösungen priorisieren die menschliche Gesundheit und das Wohlbefinden durch durchdachtes Design und Materialauswahl.'
                    },
                    technology: {
                        title: 'Innovative Technologien',
                        description: 'Wir integrieren modernste Bautechnologien, um intelligentere und effizientere Strukturen zu schaffen.'
                    },
                    carbon: {
                        title: 'CO2-Fußabdruck-Reduzierung',
                        description: 'Jedes Projekt wird mit dem Ziel der Kohlenstoffreduzierung entworfen und trägt zu einer nachhaltigeren Zukunft bei.'
                    }
                }
            },
            contact: {
                title: 'Kontakt',
                subtitle: 'Kontaktieren Sie uns für Ihr nächstes Projekt',
                address: 'Adresse',
                email: 'E-Mail',
                info: {
                    address: 'Markstraße 37, 13409 Berlin',
                    email: 'info@dldmn-baumanagement.de'
                },
                getInTouch: 'Kontakt aufnehmen',
                projectCard: {
                    title: 'Bereit, Ihr Projekt zu starten?',
                    subtitle: 'Lassen Sie uns besprechen, wie wir Ihre Bauvision zum Leben erwecken können.',
                    consultation: {
                        title: 'Beratung',
                        description: 'Kostenlose Erstberatung, um Ihre Bedürfnisse zu verstehen'
                    },
                    planning: {
                        title: 'Planung',
                        description: 'Detaillierte Projektplanung und Kostenschätzung'
                    },
                    execution: {
                        title: 'Ausführung',
                        description: 'Professionelle Ausführung mit Qualitätssicherung'
                    }
                },
                contactDescription: 'Kontaktieren Sie uns für Ihre Bau- und Investitionsbedürfnisse',
                ourLocation: 'Unser Standort',
                locationDescription: 'Besuchen Sie uns in unserem Berliner Büro',
                viewOnMaps: 'Auf Google Maps anzeigen'
            },
            footer: {
                description: 'DLDMN Baumanagement GmbH - Ihr vertrauensvoller Partner im Baumanagement.',
                quickLinks: 'Schnelle Links',
                services: 'Unsere Dienstleistungen',
                followUs: 'Folgen Sie uns',
                rights: 'Alle Rechte vorbehalten.',
                servicesList: [
                    'Baumanagement',
                    'Projektplanung',
                    'Qualitätskontrolle',
                    'Sicherheitsmanagement',
                    'Kostenschätzung',
                    'Beratungsdienstleistungen'
                ]
            },
            aboutServices: {
                title: 'Unsere Dienstleistungen',
                subtitle: 'Umfassende Lösungen für Ihre Bau- und Investitionsbedürfnisse'
            }
        }
    },
    tr: {
        translation: {
            nav: {
                home: 'Ana Sayfa',
                investment: 'Uluslararası Yatırım Danışmanlığı',
                construction: 'İnşaat ve Proje Yönetimi',
                materials: 'İnşaat Malzemeleri ve Daire Donanımı',
                ourservices: 'Hizmetlerimiz',
                sustainability: 'Sürdürülebilirlik'
            },
            hero: {
                title: 'Profesyonel İnşaat Yönetimi',
                subtitle: 'Alman Hassasiyetiyle Mükemmellik',
                description: 'DLDMN Baumanagement GmbH, 15 yılı aşkın sektör deneyimiyle kapsamlı inşaat yönetimi hizmetleri sunmaktadır.',
                cta: 'Projelerimizi Keşfedin',
                contact: 'İletişime Geçin'
            },
            about: {
                title: 'DLDMN Baumanagement Hakkında',
                subtitle: 'İnşaat Yönetiminde Mükemmellik',
                description: 'Almanya merkezli DLDMN Baumanagement GmbH, konut, ticari ve endüstriyel sektörlerde yüksek kaliteli projeler sunarak önde gelen bir inşaat yönetimi şirketi olarak kendini kanıtlamıştır.',
                experience: 'Yıllık Deneyim',
                projects: 'Tamamlanan Proje',
                clients: 'Memnun Müşteri',
                buildingExcellence: '2008\'den Beri İnşaatta Mükemmellik',
                vision: 'Vizyonumuz',
                visionText: 'Almanya\'da önde gelen inşaat yönetimi şirketi olmak ve kalite, yenilik ve sürdürülebilirlik konularında endüstri standartlarını belirlemek.',
                mission: 'Misyonumuz',
                missionText: 'Güvenlik, kalite ve çevresel sorumluluk konularında en yüksek standartları korurken müşteri beklentilerini aşan kapsamlı inşaat yönetimi hizmetleri sağlıyoruz.',
                learnMore: 'Daha fazla bilgi için',
                features: {
                    quality: {
                        title: 'Kalite Mükemmelliği',
                        description: 'Her projede Alman mühendislik standartları ve detaylara titiz dikkat.'
                    },
                    team: {
                        title: 'Uzman Ekip',
                        description: 'İnşaat yönetiminde onlarca yıllık birleşik deneyime sahip deneyimli profesyoneller.'
                    },
                    results: {
                        title: 'Kanıtlanmış Sonuçlar',
                        description: 'Projeleri zamanında ve bütçe dahilinde sürekli olarak teslim etme başarısı.'
                    }
                }
            },
            services: {
                investment: {
                    title: 'Uluslararası Yatırım Danışmanlığı',
                    description: 'DLDMN Baumanagement GmbH olarak, Almanya ve Türkiye\'de bireyler ve şirketler için profesyonel yatırım danışmanlığı hizmetleri sunuyoruz:',
                    items: [
                        'Gayrimenkul alım-satım yönetimi',
                        'Derinlemesine pazar araştırması ve analizi',
                        'Yüksek getirili yatırım fırsatlarının belirlenmesi',
                        'Hukuki, vergi ve idari takip',
                        'Maksimum kârlılık için portföy optimizasyonu',
                        'Uzun vadeli yatırım stratejilerinin geliştirilmesi'
                    ],
                    conclusion: 'Müşterilerimiz sadece yatırımlardan değil, aynı zamanda güvenli, şeffaf ve sürdürülebilir bir büyüme modelinden de faydalanır.'
                },
                construction: {
                    title: 'İnşaat ve Proje Yönetimi',
                    description: 'Konseptten tamamlanmaya kadar inşaat projenizin her aşamasını hassasiyetle yönetiyoruz.',
                    items: [
                        'Arsa geliştirme ve yeni inşaat',
                        'Modernizasyon ve renovasyon projeleri',
                        'Mimari ve mühendislik çözümleri',
                        'Şantiye yönetimi ve kalite güvencesi',
                        'Anahtar teslimi konut ve ticari projeler'
                    ],
                    conclusion: 'DLDMN Baumanagement GmbH zaman, bütçe ve kalitede verimlilik sağlar.'
                },
                materials: {
                    title: 'İnşaat Malzemeleri ve İç Mekan Çözümleri',
                    description: 'Güçlü tedarik ağımızla, Almanya ve Türkiye\'de rekabetçi fiyatlarla yüksek kaliteli malzemeler ve iç mekan çözümleri sağlıyoruz:',
                    items: [
                        'İnşaat malzemeleri (beton, çelik, yalıtım, vb.)',
                        'İç mekan çözümleri (mutfak, banyo, mobilya, akıllı ev sistemleri)',
                        'Özelleştirilmiş ve ısmarlama siparişler',
                        'Stoktan hızlı teslimat ve güvenilir lojistik'
                    ],
                    conclusion: 'Bir inşaat ortağından fazlasıyız – modern ve fonksiyonel yaşam alanları için çözüm sağlayıcısıyız.'
                }
            },
            sustainability: {
                title: 'Sürdürülebilirlik ve İnovasyon',
                subtitle: 'Gelecek İçin İnşa Etmek',
                futureOriented: 'Geleceğe Yönelik İnşaat',
                description: 'Sürdürülebilirlik taahhüdüyle geleceğe yönelik inşaata öncelik veriyoruz.',
                features: [
                    'Çevre dostu ve enerji verimli malzemeler',
                    'Sağlık bilinçli dış ve iç mekan çözümleri',
                    'Yenilikçi yapı teknolojileri',
                    'Karbon ayak izini azaltmaya odaklanan projeler'
                ],
                conclusion: 'Teslim ettiğimiz her proje doğa, insan ve teknolojinin uyumunu yansıtır.',
                commitment: 'Sürdürülebilirlik Taahhüdümüz',
                commitmentSubtitle: 'Sürdürülebilir bir gelecek inşa etmek için detaylı yaklaşım',
                ctaTitle: 'Sürdürülebilir İnşaata Hazır mısınız?',
                ctaSubtitle: 'Hem size hem de çevreye fayda sağlayacak bir proje oluşturalım.',
                ctaButton: 'Sürdürülebilir Projenizi Başlatın',
                sections: {
                    materials: {
                        title: 'Sürdürülebilir Malzemeler',
                        description: 'Çevresel etkiyi minimize eden, dayanıklılık ve performansı maksimize eden çevre dostu malzemeleri özenle seçiyoruz.'
                    },
                    health: {
                        title: 'Sağlık Bilinçli Tasarım',
                        description: 'Çözümlerimiz, düşünceli tasarım ve malzeme seçimi yoluyla insan sağlığı ve refahına öncelik verir.'
                    },
                    technology: {
                        title: 'Yenilikçi Teknolojiler',
                        description: 'Daha akıllı, daha verimli yapılar oluşturmak için en son yapı teknolojilerini entegre ediyoruz.'
                    },
                    carbon: {
                        title: 'Karbon Ayak İzi Azaltma',
                        description: 'Her proje karbon azaltımı göz önünde bulundurularak tasarlanır ve daha sürdürülebilir bir geleceğe katkıda bulunur.'
                    }
                }
            },
            contact: {
                title: 'İletişim',
                subtitle: 'Bir Sonraki Projeniz İçin Bizimle İletişime Geçin',
                address: 'Adres',
                email: 'E-posta',
                info: {
                    address: 'Markstraße 37, 13409 Berlin',
                    email: 'info@dldmn-baumanagement.de'
                },
                getInTouch: 'İletişime Geçin',
                projectCard: {
                    title: 'Projenizi Başlatmaya Hazır mısınız?',
                    subtitle: 'İnşaat vizyonunuzu hayata geçirme konusunu birlikte görüşelim.',
                    consultation: {
                        title: 'Danışmanlık',
                        description: 'İhtiyaçlarınızı anlamak için ücretsiz ilk danışmanlık'
                    },
                    planning: {
                        title: 'Planlama',
                        description: 'Detaylı proje planlaması ve maliyet tahmini'
                    },
                    execution: {
                        title: 'Uygulama',
                        description: 'Kalite güvencesi ile profesyonel uygulama'
                    }
                },
                contactDescription: 'İnşaat ve yatırım ihtiyaçlarınız için bizimle iletişime geçin',
                ourLocation: 'Konumumuz',
                locationDescription: 'Berlin ofisimizi ziyaret edin',
                viewOnMaps: 'Google Haritalar\'da Görüntüle'
            },
            footer: {
                description: 'DLDMN Baumanagement GmbH - İnşaat yönetiminde güvenilir ortağınız.',
                quickLinks: 'Hızlı Bağlantılar',
                services: 'Hizmetlerimiz',
                followUs: 'Bizi Takip Edin',
                rights: 'Tüm hakları saklıdır.',
                servicesList: [
                    'İnşaat Yönetimi',
                    'Proje Planlama',
                    'Kalite Kontrolü',
                    'Güvenlik Yönetimi',
                    'Maliyet Tahmini',
                    'Danışmanlık Hizmetleri'
                ]
            },
            aboutServices: {
                title: 'Hizmetlerimiz',
                subtitle: 'İnşaat ve yatırım ihtiyaçlarınız için kapsamlı çözümler'
            }
        }
    }
};

i18n
    .use(initReactI18next)
    .init({
        resources,
        lng: 'de',
        fallbackLng: 'en',
        interpolation: {
            escapeValue: false
        }
    });

export default i18n;

